import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X } from '../components/icons';
import { createMarketplaceListing } from '../lib/marketplace-api';
import { MARKETPLACE_CATEGORIES, NZ_REGIONS } from '../lib/marketplace-categories';
import { isLoggedIn } from '../lib/marketplace-auth';

const emptyForm = {
  category: '',
  subcategory: '',
  title: '',
  description: '',
  price: '',
  price_type: 'fixed',
  condition: 'used',
  brand: '',
  model: '',
  year: '',
  length_m: '',
  engine_hours: '',
  region: '',
  city: '',
};

export default function MarketplaceCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [images, setImages] = useState<string[]>(['']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/marketplace/signin', { replace: true });
    }
  }, [navigate]);

  const activeCategory = MARKETPLACE_CATEGORIES.find((c) => c.value === form.category);

  const set = (key: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((f) => {
      const next = { ...f, [key]: e.target.value };
      if (key === 'category') next.subcategory = '';
      return next;
    });
  };

  const setImage = (index: number, value: string) => {
    setImages((imgs) => imgs.map((img, i) => (i === index ? value : img)));
  };

  const addImage = () => {
    if (images.length < 10) setImages((imgs) => [...imgs, '']);
  };

  const removeImage = (index: number) => {
    setImages((imgs) => imgs.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.category) return setError('Please choose a category.');
    if (form.title.trim().length < 3) return setError('Please enter a title (at least 3 characters).');
    if (!form.region) return setError('Please choose your region.');
    if (form.price_type !== 'free' && form.price_type !== 'negotiable' && !form.price) {
      return setError('Please enter a price, or choose "Negotiable" / "Free".');
    }

    setLoading(true);
    try {
      const listing = await createMarketplaceListing({
        category: form.category,
        subcategory: form.subcategory || undefined,
        title: form.title.trim(),
        description: form.description.trim(),
        price: form.price ? parseFloat(form.price) : 0,
        price_type: form.price_type,
        condition: form.condition,
        brand: form.brand.trim() || undefined,
        model: form.model.trim() || undefined,
        year: form.year ? parseInt(form.year, 10) : undefined,
        length_m: form.length_m ? parseFloat(form.length_m) : undefined,
        engine_hours: form.engine_hours ? parseInt(form.engine_hours, 10) : undefined,
        region: form.region,
        city: form.city.trim() || undefined,
        images: images.map((i) => i.trim()).filter(Boolean),
      });
      navigate(`/marketplace/${listing.slug}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create listing. Please try again.');
      setLoading(false);
    }
  };

  const inputCls = 'form-input';
  const labelCls = 'block text-xs font-semibold text-text-secondary mb-2';

  return (
    <div className="max-w-[720px] mx-auto px-4 md:px-8 py-6 md:py-12 pb-24">
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
        <Link to="/marketplace" className="text-text-secondary hover:text-accent transition-colors">Marketplace</Link>
        <span>/</span>
        <span>Sell an item</span>
      </div>

      <h1 className="font-display text-2xl md:text-[1.8rem] font-bold mb-2">List an item for sale</h1>
      <p className="text-text-secondary mb-8 text-sm">
        Free to list. Your contact details stay private - buyers message you through MarineLink.
      </p>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-6 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
          <X className="w-4 h-4 mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Category */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Category *</label>
            <select className={inputCls} required value={form.category} onChange={set('category')}>
              <option value="">Choose a category…</option>
              {MARKETPLACE_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Subcategory</label>
            <select className={inputCls} value={form.subcategory} onChange={set('subcategory')} disabled={!activeCategory}>
              <option value="">{activeCategory ? 'Choose…' : 'Select a category first'}</option>
              {activeCategory?.subcategories.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Title + description */}
        <div>
          <label className={labelCls}>Title *</label>
          <input
            className={inputCls}
            type="text"
            required
            maxLength={120}
            placeholder="e.g. 2019 Yamaha F70 outboard - low hours"
            value={form.title}
            onChange={set('title')}
          />
        </div>
        <div>
          <label className={labelCls}>Description</label>
          <textarea
            className={`${inputCls} min-h-[140px]`}
            placeholder="Condition, history, reason for selling, what's included…"
            value={form.description}
            onChange={set('description')}
          />
        </div>

        {/* Price */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Price type</label>
            <select className={inputCls} value={form.price_type} onChange={set('price_type')}>
              <option value="fixed">Fixed price</option>
              <option value="negotiable">Negotiable</option>
              <option value="free">Free</option>
            </select>
          </div>
          {form.price_type !== 'free' && (
            <div>
              <label className={labelCls}>Price (NZD) {form.price_type === 'fixed' ? '*' : ''}</label>
              <input
                className={inputCls}
                type="number"
                min="0"
                step="1"
                placeholder="e.g. 4500"
                value={form.price}
                onChange={set('price')}
              />
            </div>
          )}
        </div>

        {/* Condition + specs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Condition</label>
            <select className={inputCls} value={form.condition} onChange={set('condition')}>
              <option value="new">New</option>
              <option value="used">Used</option>
              <option value="refurbished">Refurbished</option>
              <option value="parts">Parts</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Brand</label>
            <input className={inputCls} type="text" placeholder="e.g. Yamaha" value={form.brand} onChange={set('brand')} />
          </div>
          <div>
            <label className={labelCls}>Model</label>
            <input className={inputCls} type="text" placeholder="e.g. F70LA" value={form.model} onChange={set('model')} />
          </div>
          <div>
            <label className={labelCls}>Year</label>
            <input className={inputCls} type="number" min="1900" max="2100" placeholder="e.g. 2019" value={form.year} onChange={set('year')} />
          </div>
          <div>
            <label className={labelCls}>Length (m)</label>
            <input className={inputCls} type="number" min="0" step="0.1" placeholder="e.g. 5.5" value={form.length_m} onChange={set('length_m')} />
          </div>
          <div>
            <label className={labelCls}>Engine hours</label>
            <input className={inputCls} type="number" min="0" placeholder="e.g. 320" value={form.engine_hours} onChange={set('engine_hours')} />
          </div>
        </div>

        {/* Location */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Region *</label>
            <select className={inputCls} required value={form.region} onChange={set('region')}>
              <option value="">Choose a region…</option>
              {NZ_REGIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>City / town</label>
            <input className={inputCls} type="text" placeholder="e.g. Whangaparāoa" value={form.city} onChange={set('city')} />
          </div>
        </div>

        {/* Images */}
        <div>
          <label className={labelCls}>Photos (up to 10 image URLs)</label>
          <div className="space-y-2">
            {images.map((img, i) => (
              <div key={i} className="flex gap-2 items-center">
                <input
                  className={inputCls}
                  type="url"
                  placeholder="https://…"
                  value={img}
                  onChange={(e) => setImage(i, e.target.value)}
                />
                {images.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    aria-label="Remove image"
                    className="flex-shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-md text-text-muted hover:text-red-500 hover:bg-red-500/10 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {images.length < 10 && (
            <button type="button" onClick={addImage} className="text-sm text-accent hover:underline mt-2">
              + Add another photo
            </button>
          )}
          <p className="text-text-muted text-xs mt-2">
            Tip: the first photo becomes the cover image. Use direct image URLs (ending in .jpg, .png etc).
          </p>
        </div>

        <button type="submit" disabled={loading} className="btn btn-primary btn-lg btn-block">
          {loading ? 'Publishing…' : 'Publish listing'}
        </button>
      </form>
    </div>
  );
}
